import { emptyStateGenericGraphic } from '@workday/canvas-graphics-web';
import { Graphic } from '@workday/canvas-kit-react/icon';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom-v5-compat';

import PageTitle from '~/components/Shared/PageTitle/PageTitle';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingSpinner } from '~/eds/src/components/LoadingSpinner/LoadingSpinner';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { Tree } from '~/eds/src/components/Tree/Tree';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { TreeNode } from '~/eds/src/types/TreeNode';
import { Details } from '~/features/playbooks/details/Details';
import { Setup } from '~/features/playbooks/setup/Setup';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { usePermission } from '~/hooks/usePermission';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import {
  PlaybookStatus,
  PlaybookWithId,
} from '~/redux/api/types/playbooks/playbooks';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';

const clauseLibraryEditPermission = {
  resourceId: 'clause_library',
  resourceType: 'edit',
};

type PlaybookStatusFilter = 'all' | PlaybookStatus;

const Page = () => {
  const { playbookId } = useParams<{ playbookId: string }>();
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const isDraftPublishEnabled = useFlag(FlagType.PlaybookDraftPublish);
  const { navigate } = useRouting();
  const [debouncedSearch, search, setSearch] =
    useDebouncedState<Nullable<string>>('');
  const [statusFilter, setStatusFilter] = useState<PlaybookStatusFilter>('all');
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState<
    PlaybookWithId | undefined
  >();
  const [expandedPlaybookIds, setExpandedPlaybookIds] = useState<Set<string>>(
    () => new Set(),
  );
  const statusParam: PlaybookStatus | undefined =
    isDraftPublishEnabled && statusFilter !== 'all' ? statusFilter : undefined;
  const {
    data: playbooksResponse = { data: [] },
    isFetching: isFetchingPlaybook,
  } = api.endpoints.getPlaybooks.useQuery({
    search: debouncedSearch ?? '',
    status: statusParam,
  });

  const { isLoading: isLoadingPermissions, hasPermission: hasEditPermission } =
    usePermission({
      permission: clauseLibraryEditPermission,
    });

  const data = playbooksResponse.data;
  const sortedPlaybooks = useMemo(
    () =>
      isDraftPublishEnabled && statusFilter === 'all'
        ? [...data].sort((a, b) => {
            const aStatus = a.attributes.status ?? '';
            const bStatus = b.attributes.status ?? '';
            if (aStatus === 'PUBLISHED' && bStatus !== 'PUBLISHED') return -1;
            if (aStatus !== 'PUBLISHED' && bStatus === 'PUBLISHED') return 1;
            return 0;
          })
        : data,
    [data, isDraftPublishEnabled, statusFilter],
  );

  const statusFilterOptions = [
    {
      label: intl.formatMessage({
        id: 'playbooks.filter.showAll',
        defaultMessage: 'Show All',
        description: 'Filter option to show all playbooks',
      }),
      value: 'all' as const,
    },
    {
      label: intl.formatMessage({
        id: 'playbooks.filter.showDrafts',
        defaultMessage: 'Show Drafts',
        description: 'Filter option to show draft playbooks only',
      }),
      value: 'DRAFT' as const,
    },
  ];

  useEffect(() => {
    if (isFetchingPlaybook) return;
    const isCurrentInList = playbookId
      ? sortedPlaybooks.some((p) => p.id === playbookId)
      : false;
    if (playbookId && !isCurrentInList) {
      navigate('/playbooks');
    }
  }, [sortedPlaybooks, playbookId, navigate, isFetchingPlaybook]);

  useEffect(() => {
    if (playbookId) {
      setExpandedPlaybookIds((prev) => new Set(prev).add(playbookId));
    }
  }, [playbookId]);

  const playbooksTree = useMemo<TreeNode[]>(() => {
    return sortedPlaybooks.map((playbook) => {
      const status = isDraftPublishEnabled ? playbook.attributes.status : null;
      const statusLabel = status
        ? status === 'DRAFT'
          ? intl.formatMessage({
              id: 'playbooks.status.draft',
              defaultMessage: 'Draft',
              description: 'Playbook status label for draft',
            })
          : intl.formatMessage({
              id: 'playbooks.status.published',
              defaultMessage: 'Published',
              description: 'Playbook status label for published',
            })
        : null;
      const chipStatus = status === 'PUBLISHED' ? 'success' : 'warning';
      const guidelineCount = playbook.attributes.guidelines?.length ?? 0;
      return {
        key: playbook.id,
        name: playbook.attributes.name,
        isExpanded: expandedPlaybookIds.has(playbook.id),
        onClick: () => {
          navigate(`/playbooks/${playbook.id}`);
        },
        statusContent:
          isDraftPublishEnabled && statusLabel ? (
            <Layout
              align="center"
              spacing={1}
              role="status"
              styles={{
                transform: 'scale(0.85)',
                transformOrigin: 'right center',
              }}
            >
              <Chip text={statusLabel} status={chipStatus} />
              <Layout w={28} align="center" justify="end">
                <Text preset="help">{formatNumber(guidelineCount)}</Text>
              </Layout>
            </Layout>
          ) : undefined,
        children: [
          ...(playbook.attributes.guidelines ?? []).map((guideline) => ({
            key: guideline.uuid,
            name: guideline.name,
            children: [],
            parentKey: playbook.id,
          })),
        ],
      };
    });
  }, [sortedPlaybooks, expandedPlaybookIds, isDraftPublishEnabled]);

  const handleTreeUpdate = (updatedNodes: TreeNode[]) => {
    const updatedExpanded = new Set<string>();
    for (const node of updatedNodes) {
      if (node.isExpanded) updatedExpanded.add(node.key);
    }
    setExpandedPlaybookIds((prev) => {
      if (
        prev.size !== updatedExpanded.size ||
        [...updatedExpanded].some((id) => !prev.has(id))
      ) {
        return updatedExpanded;
      }
      return prev;
    });
  };

  return (
    <Layout direction="column" h="calc(100vh - 144px)">
      <Layout
        direction="column"
        spacing={8}
        bg="white"
        borderBottom="border.divider"
        pb={4}
      >
        <Layout justify="space-between" align="center">
          <PageTitle
            title={intl.formatMessage({
              id: 'playbooks.pageTitle',
              defaultMessage: 'Playbooks',
              description: 'Page title for Playbooks',
            })}
          />
          <Button
            variant="primary"
            text={intl.formatMessage({
              id: 'playbooks.createPlaybookButton',
              defaultMessage: 'Create Playbook',
              description: 'Button text to create a new playbook',
            })}
            isLoading={isLoadingPermissions}
            disabled={!hasEditPermission}
            onClick={() => setIsSetupOpen(true)}
          />
        </Layout>
        <Layout spacing={4} align="center" flex="1" minW={0}>
          <Box w="50%" minW={0}>
            <SearchInput name="search" value={search} onChange={setSearch} />
          </Box>
          {isDraftPublishEnabled && (
            <Select<PlaybookStatusFilter, false>
              enablePortal
              enableSorting={false}
              isClearable={false}
              isMulti={false}
              isSearchable={false}
              name="playbook-status-filter"
              options={statusFilterOptions}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value ?? 'all');
                navigate('/playbooks');
              }}
              width="input.m.width"
            />
          )}
        </Layout>
      </Layout>

      <Layout direction="row" flex="1" minH={0}>
        <Layout
          direction="column"
          minW="sidebar.width.s"
          maxW="sidebar.width.m"
          borderRight="border.divider"
        >
          {isFetchingPlaybook ? (
            <>
              <Layout
                as="header"
                align="center"
                justify="space-between"
                styles={{
                  height: 'header.height',
                  borderBottom: 'border.divider',
                  paddingLeft: 6,
                  paddingRight: 4,
                }}
              >
                <Text variant="body-bold" fontSize="16">
                  {intl.formatMessage({
                    id: 'playbooks.pageTitle',
                    defaultMessage: 'Playbooks',
                    description: 'Page title for Playbooks',
                  })}
                </Text>
              </Layout>
              <Layout flex="1" align="center" justify="center">
                <LoadingSpinner />
              </Layout>
            </>
          ) : (
            <>
              <Layout
                borderBottom="border.divider"
                minH="header.height"
                justify="space-between"
                px={8}
                align="center"
                bg="white"
              >
                {/* eslint-disable-next-line jsx-a11y/aria-role -- TODO: EKP-42656 */}
                <Text variant="body-bold" fontSize="l" role="subheading">
                  {intl.formatMessage({
                    id: 'playbooks.pageTitle',
                    defaultMessage: 'Playbooks',
                    description: 'Page title for Playbooks',
                  })}
                </Text>
                <Text preset="help">{sortedPlaybooks.length}</Text>
              </Layout>

              <Layout p={2} w="100%" overflowY="scroll">
                <Tree
                  expandSelectedOnSelect={false}
                  selectedNodeKey={playbookId}
                  tree={playbooksTree}
                  onUpdate={handleTreeUpdate}
                />
              </Layout>
            </>
          )}
        </Layout>
        <Layout direction="column" flex="4">
          {playbookId ? (
            <Details
              id={playbookId}
              hasEditPermission={hasEditPermission}
              onEdit={(playbook) => {
                setIsSetupOpen(true);
                setSelectedPlaybook(playbook);
              }}
            />
          ) : (
            <Layout direction="column" flex="1" align="center" justify="center">
              <Layout direction="column">
                <Graphic src={emptyStateGenericGraphic} />
                <EmptyPage preset="no-playbook-selected" />
              </Layout>
            </Layout>
          )}
        </Layout>
      </Layout>
      {isSetupOpen && (
        <Setup
          playbook={selectedPlaybook}
          onClose={() => {
            setIsSetupOpen(false);
            setSelectedPlaybook(undefined);
          }}
        />
      )}
    </Layout>
  );
};

export default Page;
